/**
 * The feature tour's itinerary. Each stop anchors to a `data-tour="<target>"`
 * attribute somewhere in the app shell or on the Dashboard — every anchor lives
 * on the home surface, so the tour never has to navigate mid-flight.
 *
 * ORDER MIRRORS THE SCREEN'S VISUAL ORDER: sidebar, then down the page (the
 * getting-started card, the net-worth hero), then the bottom of the nav, and
 * finally the two controls in the top-right corner of the chrome. That is
 * load-bearing, not incidental. This list previously ran nav → net-worth → ask →
 * getting-started, which spotlighted the bottom of the page, climbed back up,
 * then jumped to the very top; a beta user reported the tour as visually jerky
 * and correctly guessed the fix (#505). Reordering these entries moves the
 * spotlight around the screen — check the layout before changing the sequence,
 * and if the Dashboard's own order changes, change this one with it.
 *
 * `ask` moved to the tail for exactly that reason (#583). Its anchor used to be
 * the Dashboard's own Ask bar, third down the page; that launcher is gone and the
 * anchor now sits on the top bar's Ask control, beside the account menu — so the
 * step belongs with the other chrome stops, not in the middle of the page walk.
 *
 * Steps whose anchor isn't present (or isn't visible) are skipped silently:
 * the sidebar anchors don't exist on mobile, the getting-started card may be
 * dismissed or complete, and that's fine — the tour simply glides past them.
 * "Absent" and "not decided yet" are different things, though, and the walk
 * only moves forward: a surface whose anchor is still loading must say so with
 * `data-tour-pending="<target>"` or the tour will pass the step and never come
 * back (#510). See TourProvider's targetPending / tour.ts's decideStart.
 *
 * Copy is brand-neutral (no product name) so it reads correctly under a
 * white-label firm brand as well as the default Steward one.
 */
export interface TourStep {
  key: string;
  /** Matches a `data-tour` attribute value in the DOM. */
  target: string;
  title: string;
  body: string;
}

export const TOUR_STEPS: TourStep[] = [
  {
    key: 'nav',
    target: 'nav',
    title: 'How your information is organized',
    body: 'Portfolio holds your accounts, assets, and liabilities. Entities holds your trusts and companies. Documents holds your files, People holds the people you work with, and Credentials your logins and secure notes.',
  },
  {
    key: 'getting-started',
    target: 'getting-started',
    title: 'The getting started list',
    body: 'A few first steps, each one opening the page where you can do it. A step checks off on its own once the record exists, and you can dismiss the list whenever you want.',
  },
  {
    key: 'net-worth',
    target: 'net-worth',
    title: 'Your net worth',
    body: 'The total of everything in your workspace, assets minus liabilities. Some of it updates on its own: connected accounts, holdings with a ticker symbol, and properties set to use an estimated value. Everything else stays as you entered it.',
  },
  {
    key: 'settings',
    target: 'nav-settings',
    title: 'Settings',
    body: 'Connect banks and brokerages, manage your profile and sign-in security, and adjust how the app looks and behaves.',
  },
  {
    key: 'feedback',
    target: 'nav-feedback',
    title: 'Send us feedback',
    body: 'Share an idea, report a problem, or tell us what is not working.',
  },
  {
    key: 'ask',
    target: 'ask',
    title: 'Ask a question',
    // This step used to explain the wide-screen side panel (docs/24 T4). Matt cut that
    // sentence on 2026-10-20, so the tour no longer mentions the panel; the assistant's own
    // guide (steward-guide.ts) still explains it when someone asks.
    body: 'Ask about your accounts, your balances, or how something here works. Answers come from the records and balances you can already see.',
  },
  {
    key: 'account',
    target: 'account-menu',
    title: 'Your account menu',
    body: 'Reopen the getting started list or retake this tour from here at any time. Signing out is here too. We’re glad you’re here.',
  },
];
